import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import BillingAddress from '../../../components/V2/BillingAddress';
import WeekSlider from './WeekSlider';
import OrderList from './OrderList';
import DeliveryDay from './DeliveryDay';
import { EditWeekOrder } from './components';
import { useDeliveryDays, useCutoffTime } from './hooks';
import SplitPageSidebar from 'core/ui/components/SplitPageSidebar';
import ResponsiveView from 'core/ui/components/ResponsiveView';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import { Box, Container, Flex, H3, Hr, Page, Text } from 'core/ui/atoms';

function UserManagementPage() {
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

  // Fetch delivery days
  const {
    loading: deliveryDaysLoading,
    error: deliveryDaysError,
    deliveryDays,
    firstPeriod,
  } = useDeliveryDays();

  // Current selected delivery period
  const currentDeliveryPeriod = deliveryDays[selectedWeekIndex] || firstPeriod;

  // Fetch cutoff time for selected period
  const {
    loading: cutoffTimeLoading,
    error: cutoffTimeError,
    cutoffDateTime,
    formattedDateTime,
  } = useCutoffTime(currentDeliveryPeriod?.id);

  // Handle week selection change
  const handleWeekChange = (deliveryDayId) => {
    const weekIndex = deliveryDays.findIndex((day) => day.id === deliveryDayId);
    if (weekIndex !== -1) {
      setSelectedWeekIndex(weekIndex);
    }
  };

  // Loading states
  const isLoadingDeliveryDays = isLoading(deliveryDaysLoading, deliveryDays, deliveryDaysError);
  const isLoadingCutoffTime = isLoading(cutoffTimeLoading, formattedDateTime.time, cutoffTimeError);

  if (isLoadingDeliveryDays) {
    return <Loading />;
  }

  if (!firstPeriod) {
    return null;
  }

  if (isLoadingCutoffTime) {
    return <Loading />;
  }

  const ordersDescriptionText = () => {
    const now = moment();
    const cutoffDate = moment(cutoffDateTime);
    const TenDaysLater = cutoffDate.add(2, 'days');
    if (cutoffDate.isBefore(now)) {
      return `The cut-off to edit this order has passed. If you need to make changes to your order, email us at help@gomethodology.com by 9:00 AM PT on ${TenDaysLater.format('dddd, MMMM Do')}.`;
    }
    if (cutoffDate.isAfter(now)) {
      return `Edit this week's order until ${formattedDateTime.time} PST on ${formattedDateTime.date}`;
    }
  };

  const RenderSideBar = () => (
    <LeftSideBarContainer>
      <EditWeekOrder deliveryPeriodId={currentDeliveryPeriod?.id} />
      <AddressTitle>This Week's Delivery Address</AddressTitle>
      <BillingAddress />
      <DeliveryDay />
    </LeftSideBarContainer>
  );

  return (
    <Page scrollTop>
      <ResponsiveView desktop={true}>
        <OrdersHeadingDesktop>Weekly Menu</OrdersHeadingDesktop>
        <Hr />
        <OrdersDescriptionDesktop>{ordersDescriptionText()}</OrdersDescriptionDesktop>
      </ResponsiveView>
      <SplitPageSidebar sidebarView={<RenderSideBar />}>
        <MainContainer>
          <DashboardSection>
            <OrdersHeading>Weekly Menu</OrdersHeading>
            <OrdersDescription>{ordersDescriptionText()}</OrdersDescription>
            <WeekSlider
              defaultWeek={firstPeriod}
              deliveryPeriods={deliveryDays}
              selectedWeek={selectedWeekIndex}
              onChangeWeek={handleWeekChange}
            />
            <ContentContainer direction='column' alignItems='center'>
              <OrderList deliveryPeriod={currentDeliveryPeriod} />
              <Divider />
              <AddressSection>
                <AddressTitle>This Week's Delivery Address</AddressTitle>
                <BillingAddress />
              </AddressSection>
              <ResponsiveView desktop={false}>
                <DeliveryDay />
              </ResponsiveView>
            </ContentContainer>
          </DashboardSection>
        </MainContainer>
      </SplitPageSidebar>
    </Page>
  );
}

const LeftSideBarContainer = styled(Box)`
  width: 80%;
  ${(p) => p.theme.max('md')`
    display: none;
  `}
`;

const Divider = styled(Hr)`
  display: none;
  ${(p) => p.theme.max('md')`
    display: block;
  `}
`;

const MainContainer = styled.div`
  ${(p) => p.theme.max('md')`
    border-radius: 40px 40px 0 0;
  `}
`;

const DashboardSection = styled(Container)`
  position: relative;
  padding: 0;

  ${(p) => p.theme.max('sm')`
    padding: 20px 0 0 0;
  `}
`;

const SectionHeading = styled(H3)`
  font-size: 1.25em;
  font-weight: 500;
  margin-top: 0;
  text-transform: capitalize;

  ${(p) => p.theme.max('sm')`
    font-size: 0.875em;
  `}
`;

const OrdersHeadingDesktop = styled(SectionHeading)`
  margin: 0;
  color: ${(p) => p.theme.typography.colors.primary};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  text-align: center;
  padding-top: 24px;
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.96px;

  ${(p) => p.theme.max('sm')`
    display: none;
  `}
`;

const OrdersDescriptionDesktop = styled.p`
  text-align: center;
  color: ${(p) => p.theme.typography.colors.primary};
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  ${(p) => p.theme.max('sm')`
    display: none;
  `}
`;

const OrdersHeading = styled(SectionHeading)`
  margin: 0;
  top: 30px;
  left: 40px;
  color: ${(p) => p.theme.typography.colors.primary};
  display: none;

  ${(p) => p.theme.max('sm')`
    position: sticky;
    top: 60px;
    bottom: 0;
    background: white;
    padding-top: 20px;
    padding-bottom: 0px;
    height: 67px;
    font-size: 30px;
    text-align: center;
    font-family: ${({ theme }) => theme.typography.familyLarken};
    display: block;
    z-index: 1;
  `}
`;

const OrdersDescription = styled.p`
  padding: 0 70px;
  margin: 0 0 27px 0;
  display: none;

  ${(p) => p.theme.max('sm')`
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    font-family: ${({ theme }) => theme.typography.familyPoppins};
    display: block;
  `}
`;

const ContentContainer = styled(Flex)``;

const AddressSection = styled(Box)`
  display: none;
  ${(p) => p.theme.max('sm')`
    display: block;
    width: 90%;
  `}
`;

const AddressTitle = styled(Text)`
  text-align: start;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  ${(p) => p.theme.max('sm')`
    text-align: center;
    font-size: 22px;
  `}
`;


export default UserManagementPage;
